import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { AppDialog } from "@/components/app/AppDialog";
import { ApiError } from "@/lib/apiClient";
import { usePaatProfilesList } from "@/lib/paatProfiles";
import {
  useDeactivateToken,
  useDeleteToken,
  useSetTokenTags,
  useSetTokenExpiration,
  useChangePaatProfile,
  useChangePassword,
  useGenerateOtp,
  type GenerateOtpResult,
  type TokenSummary,
} from "@/lib/tokens";
import { formatDateTime } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";

/**
 * Sdílené akční modaly nad tokenem (deaktivace / smazání / tagy / expirace / PAAT).
 * Reuse v přehledu ({@code TokensPage}) i na detailu ({@code TokenDetailPage}).
 */

export const tokenInputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

export function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") return body.message ?? body.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function toLocalInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 16) : "";
}

export function ErrBox({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{msg}</div>;
}

/**
 * Průběh u akcí, které volají auth server. Bez něj vypadá čekání jako zamrzlé okno:
 * když brána neodpovídá, konektor požadavek jednou zopakuje a s výchozím časovým
 * limitem 30 s se odpověď (chyba) vrátí až po minutě.
 */
export function PendingNote({ show }: { show: boolean }) {
  const { t } = useTranslation();
  if (!show) return null;
  return <div className="text-sm text-tertiary">{t("tokens.action.waitingForAuthServer")}</div>;
}

export function DeactivateDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useDeactivateToken();
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await m.mutateAsync(token.tokenId);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.deactivate")}
      description={t("tokens.deactivateConfirm", { tokenId: token.tokenId })}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary-destructive" onPress={() => void submit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("tokens.action.deactivate")}</Button>
        </>
      }
    >
      <PendingNote show={m.isPending} />
      <ErrBox msg={err} />
    </AppDialog>
  );
}

export function DeleteDialog({
  token,
  onClose,
  onDeleted,
}: {
  token: TokenSummary;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();
  const m = useDeleteToken();
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await m.mutateAsync(token.tokenId);
      onClose();
      onDeleted?.();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.delete")}
      description={t("tokens.deleteConfirm", { tokenId: token.tokenId })}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary-destructive" onPress={() => void submit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("tokens.action.delete")}</Button>
        </>
      }
    >
      <ErrBox msg={err} />
    </AppDialog>
  );
}

export function TagsDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useSetTokenTags();
  const [value, setValue] = useState((token.tags ?? []).join(", "));
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    const tags = value.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
    try {
      await m.mutateAsync({ tokenId: token.tokenId, tags });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.tags")}
      description={token.tokenId}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.tagsLabel")}</span>
          <input className={tokenInputCls} value={value} onChange={(e) => setValue(e.target.value)} placeholder="tag1, tag2" />
        </label>
        <ErrBox msg={err} />
      </div>
    </AppDialog>
  );
}

export function ExpirationDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useSetTokenExpiration();
  const [value, setValue] = useState(toLocalInput(token.expiresAt));
  const [seconds, setSeconds] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const save = async (expiresAt: string | null, expiresInSeconds?: number | null) => {
    setErr(null);
    try {
      await m.mutateAsync({ tokenId: token.tokenId, expiresAt, expiresInSeconds });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  const saveSubmit = () => {
    const s = seconds.trim();
    if (s !== "") return save(null, parseInt(s, 10)); // TTL v sekundách má přednost
    return save(value ? value : null);
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.expiration")}
      description={token.tokenId}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="tertiary" onPress={() => void save(null)} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("tokens.expiration.clear")}</Button>
          <Button color="primary" onPress={() => void saveSubmit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.field.expires")}</span>
          <input type="datetime-local" className={tokenInputCls} value={value} onChange={(e) => setValue(e.target.value)} disabled={seconds.trim() !== ""} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.expiration.ttlSeconds")}</span>
          <input type="number" min={1} className={tokenInputCls} value={seconds} onChange={(e) => setSeconds(e.target.value)} placeholder="např. 3600" />
          <span className="text-xs text-tertiary">{t("tokens.expiration.ttlHint")}</span>
        </label>
        <ErrBox msg={err} />
      </div>
    </AppDialog>
  );
}

export function PaatDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useChangePaatProfile();
  const profiles = usePaatProfilesList();
  const [value, setValue] = useState(token.paatProfileId ?? "");
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await m.mutateAsync({ tokenId: token.tokenId, paatProfileId: value });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.paat")}
      description={token.tokenId}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={m.isPending || !value}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="rounded-lg bg-primary px-3 py-2 text-xs text-warning-primary ring-1 ring-warning">{t("tokens.paat.warning")}</div>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.col.paat")}</span>
          <AppSelect value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">{t("paat.selectPlatform")}</option>
            {profiles.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id} ({p.platform})
              </option>
            ))}
          </AppSelect>
        </label>
        <ErrBox msg={err} />
      </div>
    </AppDialog>
  );
}

/** Změna hesla tokenu (staré + nové). */
export function ChangePasswordDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useChangePassword();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!oldPassword || !newPassword) return setErr(t("tokens.pw.required"));
    try {
      await m.mutateAsync({ tokenId: token.tokenId, oldPassword, newPassword });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.changePassword")}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("common.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.pw.old")}</span>
          <input className={tokenInputCls} type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.pw.new")}</span>
          <input className={tokenInputCls} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </label>
        <ErrBox msg={err} />
      </div>
    </AppDialog>
  );
}

/** Generování OTP z hesla (zobrazí OTP + expiraci). */
export function GenerateOtpDialog({ token, onClose }: { token: TokenSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useGenerateOtp();
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<GenerateOtpResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    setResult(null);
    if (!password) return setErr(t("tokens.pw.required"));
    try {
      setResult(await m.mutateAsync({ tokenId: token.tokenId, password }));
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("tokens.action.generateOtp")}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={m.isPending}>{t("common.close")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={m.isPending} isLoading={m.isPending} showTextWhileLoading>{t("tokens.action.generateOtp")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("tokens.pw.label")}</span>
          <input className={tokenInputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {result && result.otp && (
          <div className="rounded-lg bg-secondary p-4 text-center ring-1 ring-secondary">
            <p className="font-mono text-2xl font-semibold tracking-widest text-primary">{result.otp}</p>
            {result.otpExpiresAt && (
              <p className="mt-1 text-xs text-tertiary">{t("tokens.otp.expiresAt")}: {formatDateTime(result.otpExpiresAt)}</p>
            )}
          </div>
        )}
        {result && !result.otp && result.message && <ErrBox msg={result.message} />}
        <ErrBox msg={err} />
      </div>
    </AppDialog>
  );
}
